import { SvgCallToAction } from '@loomhq/lens';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  endCTAFlow,
  endThumbnailFlow,
  endVideoSettingsFlow,
  startCTAFlow,
} from 'actions/editor';
import {
  requestVideoPlayerLeaveFullScreen,
  updateTheaterMode,
} from 'actions/videoPlayer';
import { toggleViewerInsightsUI } from 'actions/viewerInsights';
import {
  ADD_CALL_TO_ACTION_UPGRADE_BUTTON_CLICKED,
  ADD_CUSTOM_VIDEO_CTA_CLICKED,
} from 'constants/events';
import { handleToggleTranscriptCreator } from 'creators/shareVideo';
import { hasScope } from 'selectors/scopes';
import { selectIsActive } from 'selectors/viewerInsights';
import * as analytics from 'utilities/analytics';
import { isMobile } from 'utilities/device';

import { useFeatureFlagValue } from 'hooks/featureFlag';

import CollapsibleSideButton from '../collapsible-side-button';
import { EXPERIMENT_RENAME_CTA } from '../../../../../shared/constants/featureFlag';
import { VIDEO_CTA_ACCESS } from '../../../../../shared/constants/scopes';

import UpgradeTooltip from 'components/upgrade-tooltip';

const AddVideoCtaButton = props => {
  const { disabled, isCollapsed } = props;

  const {
    areVideoCtaSettingsOpen,
    areVideoSettingsOpen,
    areViewerInsightsOpen,
    areThumbnailSettingsOpen,
    ctaEnabled,
    hasCTAScope,
    isVideoPlayerFullScreen,
    userCanEditVideo,
    // isTranscriptVisible,
    // hasCTAScope
  } = useSelector(state => {
    return {
      areVideoCtaSettingsOpen: state.editor?.cta_flow,
      areVideoSettingsOpen: state.editor?.video_settings_flow,
      areViewerInsightsOpen: selectIsActive(state),
      areThumbnailSettingsOpen: state.editor?.thumbnail_flow,
      ctaEnabled: state.share_video.cta_enabled,
      hasCTAScope: hasScope(state, VIDEO_CTA_ACCESS),
      isVideoPlayerFullScreen: state.video_player?.full_screen,
      userCanEditVideo: state.share_video.video.currentUserCanEdit,
    };
  });

  const dispatch = useDispatch();

  const handleClick = () => {
    const { isTranscriptVisible } = props;

    if (areVideoCtaSettingsOpen) {
      dispatch(endCTAFlow());

      return;
    }

    analytics.track(ADD_CUSTOM_VIDEO_CTA_CLICKED);

    if (areViewerInsightsOpen) {
      dispatch(toggleViewerInsightsUI());
    }

    if (areVideoSettingsOpen) {
      dispatch(endVideoSettingsFlow());
    }

    if (areThumbnailSettingsOpen) {
      dispatch(endThumbnailFlow());
    }

    if (isTranscriptVisible) {
      dispatch(handleToggleTranscriptCreator());
    }

    dispatch(updateTheaterMode(false));

    // we only request leaving full screen if already full screen,
    // otherwise we queue up the request to leave full screen
    if (isVideoPlayerFullScreen) {
      dispatch(requestVideoPlayerLeaveFullScreen());
    }

    dispatch(startCTAFlow());
  };

  const renameCTAFlag = useFeatureFlagValue(EXPERIMENT_RENAME_CTA);

  // Show if url arg is there or if the user has already enabled it
  let button = null;

  if (hasCTAScope) {
    const buttonText = (() => {
      if (renameCTAFlag) {
        return ctaEnabled ? 'Edit link' : 'Add a link';
      }

      return ctaEnabled ? 'Edit call-to-action' : 'Call-to-action';
    })();

    button = (
      <CollapsibleSideButton
        icon={<SvgCallToAction />}
        isCollapsed={isCollapsed}
        isDisabled={disabled}
        onClick={handleClick}
      >
        {buttonText}
      </CollapsibleSideButton>
    );
  } else {
    button = (
      <UpgradeTooltip
        analyticsEvent={ADD_CALL_TO_ACTION_UPGRADE_BUTTON_CLICKED}
        placement="leftCenter"
        title="Add a call-to-action"
      >
        <CollapsibleSideButton
          icon={<SvgCallToAction />}
          isCollapsed={isCollapsed}
          isDisabled
          isUpgrade
        >
          {renameCTAFlag ? 'Add a link' : 'Call-to-action'}
        </CollapsibleSideButton>
      </UpgradeTooltip>
    );
  }

  return userCanEditVideo && !isMobile && button;
};

export default AddVideoCtaButton;
